import { cx } from '@linaria/core';
import { useQuery } from '@tanstack/react-query';
import { useRef, useMemo } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { useUserIdentityState } from '@provider/user-identity';
import { Select } from '@ui/kit';
import { useActivityIndicator, CSSRefTransition } from '@utils/react';
import { EmptyMessage } from '../../../atoms/EmptyMessage/EmptyMessage.js';
import { Loader } from '../../../atoms/loader.js';
import { heading, header } from '../../shared.header.style.js';
import { getPortalsForUserQuery } from '../api.get-for-user.js';
import { portalsStore } from '../state.store.js';
import { sortPortals } from '../util.sort.js';
import { PortalErrorMessage } from './message.error.js';
import { Portal } from './molecule.portal.js';
import { pageCls, ENTER_TRANSITION_DURATION_MS, transitionCls, select, header as header$1, content, LIST_TRANSITION_DURATION, listCls } from './page.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

const options = [{
  id: "lexicographic",
  label: "Alfabetisk"
}, {
  id: "grade_level",
  label: "Niveau"
}];
const Portals = () => {
  const containerRef = useRef(null);
  const user = useUserIdentityState(state => state.user);
  const {
    data,
    isError,
    isLoading
  } = useQuery({
    ...getPortalsForUserQuery(user),
    // data fetching must have been handled before displaying the page
    enabled: false
  });
  const activityIndicator = useActivityIndicator(isLoading, {
    allowInitial: true
  });
  const sortedBy = portalsStore.useSelector(state => state.sortedBy);
  const sortedPortals = useMemo(() => sortPortals(data, sortedBy), [data, sortedBy]);
  return /*#__PURE__*/jsx("div", {
    className: pageCls,
    children: /*#__PURE__*/jsxs(TransitionGroup, {
      component: null,
      children: [activityIndicator.isActive() && /*#__PURE__*/jsx(CSSRefTransition, {
        classNames: transitionCls,
        timeout: ENTER_TRANSITION_DURATION_MS,
        children: ref => /*#__PURE__*/jsx(Loader, {
          ref: ref
        })
      }, "loader"), !activityIndicator.isActive() && /*#__PURE__*/jsx(CSSRefTransition, {
        classNames: transitionCls,
        timeout: ENTER_TRANSITION_DURATION_MS,
        children: ref => /*#__PURE__*/jsxs("div", {
          ref: ref,
          children: [/*#__PURE__*/jsxs("header", {
            className: cx(header, header$1),
            ref: containerRef,
            children: [/*#__PURE__*/jsx("div", {
              className: select,
              "data-placeholder": true
            }), /*#__PURE__*/jsx("h1", {
              className: heading,
              children: "Fagportaler"
            }), /*#__PURE__*/jsx(Select, {
              className: select,
              theme: "compact",
              label: "Sort\xE9r",
              selectedValues: options.filter(it => it.id === sortedBy).map(it => it.id),
              options: options,
              toggleValue: nextOption => portalsStore.dispatch(portalsStore.actions.sort(nextOption)),
              collapseOnClick: true
            })]
          }), /*#__PURE__*/jsxs(TransitionGroup, {
            className: content,
            children: [isError && /*#__PURE__*/jsx(CSSRefTransition, {
              classNames: transitionCls,
              timeout: ENTER_TRANSITION_DURATION_MS,
              children: ref => /*#__PURE__*/jsx(PortalErrorMessage, {
                ref: ref
              })
            }, "error"), !isError && !!sortedPortals?.length && /*#__PURE__*/jsx(CSSRefTransition, {
              classNames: transitionCls,
              timeout: ENTER_TRANSITION_DURATION_MS,
              children: ref => /*#__PURE__*/jsx(TransitionGroup, {
                component: null,
                children: /*#__PURE__*/jsx(CSSRefTransition, {
                  classNames: listCls,
                  timeout: {
                    enter: LIST_TRANSITION_DURATION,
                    exit: 0
                  },
                  nodeRef: ref,
                  children: ref => /*#__PURE__*/jsx("ul", {
                    className: listCls,
                    ref: ref,
                    children: sortedPortals.map(it => /*#__PURE__*/jsx(Portal, {
                      portal: it
                    }, it.licenseKey))
                  })
                }, `list.${sortedBy}`)
              })
            }, "data"), !isError && !sortedPortals?.length && /*#__PURE__*/jsx(CSSRefTransition, {
              classNames: transitionCls,
              timeout: ENTER_TRANSITION_DURATION_MS,
              children: ref => /*#__PURE__*/jsx(EmptyMessage, {
                ref: ref,
                message: "Der er ingen tilg\xE6ngelige fagportaler"
              })
            }, "empty")]
          })]
        })
      }, `content-${sortedBy}`)]
    })
  });
};

export { Portals };
//# sourceMappingURL=page.js.map
